/**
 * ELDA Theme System
 *
 * Comprehensive theme configuration for light and dark modes.
 * All colors are defined here and can be used throughout the application.
 */

export const colors = {
  // Primary Brand Colors
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main blue
    600: '#2563eb', // Primary button, links - more vibrant
    700: '#1d4ed8', // Hover states
    800: '#1e40af',
    900: '#1e3a8a',
  },

  // Neutral Grays
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // Semantic Colors
  red: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444', // Error, danger
    600: '#dc2626', // Error hover
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  green: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e', // Success
    600: '#16a34a', // Success hover
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  amber: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b', // Warning
    600: '#d97706', // Warning hover
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  orange: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316', // High priority
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },

  emerald: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981', // Alternative success
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },

  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7e22ce',
    800: '#6b21a8',
    900: '#581c87',
  },

  indigo: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#6366f1',
    600: '#4f46e5',
    700: '#4338ca',
    800: '#3730a3',
    900: '#312e81',
  },

  cyan: {
    50: '#ecfeff',
    100: '#cffafe',
    200: '#a5f3fc',
    300: '#67e8f9',
    400: '#22d3ee',
    500: '#06b6d4',
    600: '#0891b2',
    700: '#0e7490',
    800: '#155e75',
    900: '#164e63',
  },

  // Special colors
  white: '#ffffff',
  black: '#000000',
}

// ============================================================================
// LIGHT THEME
// ============================================================================

export const lightTheme = {
  // Base colors
  background: {
    primary: '#f2f3f5', // Custom grey background
    secondary: colors.gray[50],
    tertiary: colors.gray[100],
    hover: colors.gray[50],
    active: colors.gray[100],
  },

  // Brand colors (navy and gold from the logo), used by the login page
  brand: {
    navy: '#0b1f3a',
    gold: '#F6A945',
    primary: { bg: '#0b1f3a', bgHover: '#14315a', text: colors.white },
    accent: { text: '#9a5f12', textHover: '#b8731a' },
    glass: {
      bg: 'rgba(255, 255, 255, 0.72)',
      bgEnd: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.75)',
      highlight: 'rgba(255, 255, 255, 0.9)',
    },
  },

  // Surface colors (cards, modals, panels)
  surface: {
    primary: colors.white,
    secondary: colors.gray[50],
    elevated: colors.white,
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  // Text colors
  text: {
    primary: colors.gray[900], // Main text (black)
    secondary: colors.gray[700], // Secondary text
    tertiary: colors.gray[600], // Tertiary text
    disabled: colors.gray[400], // Disabled text
    inverse: colors.white, // Text on dark backgrounds
    link: colors.blue[600], // Links
    linkHover: colors.blue[700], // Link hover
  },

  // Border colors
  border: {
    primary: colors.gray[200],
    secondary: colors.gray[300],
    focus: colors.blue[500],
    error: colors.red[500],
    success: colors.green[500],
  },

  // Button colors
  button: {
    primary: {
      bg: colors.blue[600], // #2563eb - vibrant blue
      bgHover: colors.blue[700],
      bgActive: colors.blue[800],
      text: colors.white,
      border: colors.blue[600],
      icon: colors.blue[600],
    },
    secondary: {
      bg: colors.gray[100],
      bgHover: colors.gray[200],
      bgActive: colors.gray[300],
      text: colors.gray[700],
      border: colors.gray[300],
    },
    outline: {
      bg: 'transparent',
      bgHover: colors.gray[50],
      bgActive: colors.gray[100],
      text: colors.gray[700],
      border: colors.gray[300],
    },
    ghost: {
      bg: 'transparent',
      bgHover: colors.gray[100],
      bgActive: colors.gray[200],
      text: colors.gray[700],
      border: 'transparent',
    },
    danger: {
      bg: colors.red[600],
      bgHover: colors.red[700],
      bgActive: colors.red[800],
      text: colors.white,
      border: colors.red[600],
    },
    success: {
      bg: colors.green[600],
      bgHover: colors.green[700],
      bgActive: colors.green[800],
      text: colors.white,
      border: colors.green[600],
    },
    disabled: {
      bg: colors.gray[300],
      text: colors.gray[500],
      border: colors.gray[300],
    },
  },

  // Input colors
  input: {
    bg: colors.white,
    bgHover: colors.white,
    bgFocus: colors.white,
    bgDisabled: colors.gray[100],
    border: colors.gray[300],
    borderHover: colors.gray[400],
    borderFocus: colors.blue[500],
    borderError: colors.red[500],
    text: colors.gray[900],
    textPlaceholder: colors.gray[400],
    textPlaceholderMuted: colors.gray[500],
    placeholderLetterSpacing: '0.04em',
    textDisabled: colors.gray[500],
  },

  // Status colors
  status: {
    info: {
      bg: colors.blue[50],
      bgHover: colors.blue[100],
      text: colors.blue[900],
      border: colors.blue[200],
      icon: colors.blue[600],
    },
    success: {
      bg: colors.green[50],
      bgHover: colors.green[100],
      text: colors.green[900],
      border: colors.green[200],
      icon: colors.green[600],
    },
    warning: {
      bg: colors.amber[50],
      bgHover: colors.amber[100],
      text: colors.amber[900],
      border: colors.amber[200],
      icon: colors.amber[600],
    },
    error: {
      bg: colors.red[50],
      bgHover: colors.red[100],
      text: colors.red[900],
      border: colors.red[200],
      icon: colors.red[600],
    },
  },

  // Navigation colors
  nav: {
    bg: colors.white,
    bgHover: colors.gray[100],
    bgActive: colors.blue[50],
    text: colors.gray[700],
    textHover: colors.gray[900],
    textActive: colors.blue[600],
    border: colors.gray[200],
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  },

  // Card colors
  card: {
    bg: colors.white,
    bgHover: colors.white,
    border: colors.gray[200],
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    shadowHover: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    shadowElevated:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    shadowLarge:
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Section/Block separation
  section: {
    border: colors.gray[200],
    borderStrong: colors.gray[300],
    shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    shadowStrong:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    bg: colors.white,
    bgSubtle: colors.gray[50],
  },

  // Modal colors
  modal: {
    bg: colors.white,
    overlay: 'rgba(0, 0, 0, 0.5)',
    border: colors.gray[200],
    shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },

  // Table colors
  table: {
    bg: colors.white,
    bgHeader: colors.gray[50],
    bgRow: colors.white,
    bgRowHover: colors.gray[50],
    bgRowSelected: colors.blue[50],
    border: colors.gray[200],
    text: colors.gray[900],
    textHeader: colors.gray[700],
  },

  // Badge colors
  badge: {
    default: {
      bg: colors.gray[100],
      text: colors.gray[700],
    },
    primary: {
      bg: colors.blue[100],
      text: colors.blue[700],
    },
    success: {
      bg: colors.green[100],
      text: colors.green[700],
    },
    warning: {
      bg: colors.amber[100],
      text: colors.amber[700],
    },
    error: {
      bg: colors.red[100],
      text: colors.red[700],
    },
    purple: {
      bg: colors.purple[100],
      text: colors.purple[700],
    },
    indigo: {
      bg: colors.indigo[100],
      text: colors.indigo[700],
    },
  },

  // Priority colors (for light theme - lighter backgrounds with darker text - outlined style)
  priority: {
    low: {
      bg: colors.green[200], // More green background
      text: colors.green[600], // More vibrant green text
    },
    medium: {
      bg: colors.amber[50], // Clearer, less amber background
      text: colors.amber[600], // Clearer amber text
    },
    high: {
      bg: colors.orange[200], // More intense orange background
      text: colors.orange[800], // More intense orange text
    },
    urgent: {
      bg: colors.red[100], // Light red background
      text: colors.red[700], // Dark red text
    },
  },

  // Chart colors (for data visualization)
  chart: {
    colors: [
      colors.blue[500],
      colors.green[500],
      colors.amber[500],
      colors.red[500],
      colors.purple[500],
      colors.cyan[500],
      colors.indigo[500],
      colors.emerald[500],
      colors.purple[400],
      colors.indigo[400],
    ],
  },

  // Work type colors (specific to ELDA)
  workType: {
    damage: colors.blue[500],
    bullage_neige: colors.cyan[500],
    bullage: colors.cyan[400],
    treuil: colors.purple[500],
    transport_materiel: colors.amber[500],
    club_sports: colors.emerald[500],
    esf: colors.green[500],
    ski_show: colors.cyan[600],
    grand_prix: colors.cyan[700],
    secours: colors.red[600],
    carburant: colors.green[400],
    panne: colors.red[500],
    formation: colors.purple[400],
    maintenance: colors.amber[600],
    deplacement_enneigeurs: colors.purple[600],
    deneigement_regards: colors.purple[400],
    point_organisation: colors.gray[500],
    debut_fin_poste: colors.gray[400],
    other: colors.gray[600],
  },
} as const

// ============================================================================
// DARK THEME
// ============================================================================

export const darkTheme = {
  // Base colors - Using warmer slate tones instead of grey-blue
  background: {
    primary: '#0f172a', // slate-900 - warmer dark background
    secondary: '#1e293b', // slate-800 - slightly lighter
    tertiary: '#334155', // slate-700
    hover: '#1e293b', // slate-800
    active: '#334155', // slate-700
  },

  // Brand colors (navy and gold from the logo), used by the login page
  brand: {
    navy: '#0b1f3a',
    gold: '#F6A945',
    primary: { bg: '#F6A945', bgHover: '#ffbe63', text: '#0b1f3a' },
    accent: { text: '#F6A945', textHover: '#ffd08a' },
    glass: {
      bg: 'rgba(11, 31, 58, 0.66)',
      bgEnd: 'rgba(11, 31, 58, 0.42)',
      border: 'rgba(255, 255, 255, 0.22)',
      highlight: 'rgba(255, 255, 255, 0.35)',
    },
  },

  // Surface colors (cards, modals, panels) - Using slate for consistency
  surface: {
    primary: '#1e293b', // slate-800
    secondary: '#334155', // slate-700
    elevated: '#0f172a', // slate-900 - darker for elevated surfaces
    overlay: 'rgba(0, 0, 0, 0.7)',
  },

  // Text colors
  text: {
    primary: colors.gray[100], // Main text (almost white)
    secondary: colors.gray[300], // Secondary text
    tertiary: colors.gray[400], // Tertiary text
    disabled: colors.gray[600], // Disabled text
    inverse: colors.gray[900], // Text on light backgrounds
    link: colors.blue[400], // Links
    linkHover: colors.blue[300], // Link hover
  },

  // Border colors - Using slate tones
  border: {
    primary: '#334155', // slate-700
    secondary: '#475569', // slate-600
    focus: colors.blue[500],
    error: colors.red[500],
    success: colors.green[500],
  },

  // Button colors
  button: {
    primary: {
      bg: colors.blue[500],
      bgHover: colors.blue[600],
      bgActive: colors.blue[700],
      text: colors.white,
      border: colors.blue[500],
    },
    secondary: {
      bg: '#334155', // slate-700 - softer than gray
      bgHover: '#475569', // slate-600
      bgActive: '#64748b', // slate-500
      text: colors.gray[200],
      border: '#475569', // slate-600
    },
    outline: {
      bg: 'transparent',
      bgHover: colors.gray[800],
      bgActive: colors.gray[700],
      text: colors.gray[300],
      border: colors.gray[600],
    },
    ghost: {
      bg: 'transparent',
      bgHover: colors.gray[800],
      bgActive: colors.gray[700],
      text: colors.gray[300],
      border: 'transparent',
    },
    danger: {
      bg: colors.red[600],
      bgHover: colors.red[700],
      bgActive: colors.red[800],
      text: colors.white,
      border: colors.red[600],
    },
    success: {
      bg: colors.green[600],
      bgHover: colors.green[700],
      bgActive: colors.green[800],
      text: colors.white,
      border: colors.green[600],
    },
    disabled: {
      bg: colors.gray[800],
      text: colors.gray[600],
      border: colors.gray[700],
    },
  },

  // Input colors - Using slate tones
  input: {
    bg: '#334155', // slate-700
    bgHover: '#334155',
    bgFocus: '#334155',
    bgDisabled: '#1e293b', // slate-800
    border: '#475569', // slate-600
    borderHover: '#64748b', // slate-500
    borderFocus: colors.blue[500],
    borderError: colors.red[500],
    text: colors.gray[100],
    textPlaceholder: colors.gray[400],
    textPlaceholderMuted: colors.gray[500],
    placeholderLetterSpacing: '0.04em',
    textDisabled: colors.gray[600],
  },

  // Status colors
  status: {
    info: {
      bg: colors.blue[900],
      bgHover: colors.blue[800],
      text: colors.blue[200],
      border: colors.blue[700],
      icon: colors.blue[400],
    },
    success: {
      bg: colors.green[900],
      bgHover: colors.green[800],
      text: colors.green[200],
      border: colors.green[700],
      icon: colors.green[400],
    },
    warning: {
      bg: colors.amber[900],
      bgHover: colors.amber[800],
      text: colors.amber[200],
      border: colors.amber[700],
      icon: colors.amber[400],
    },
    error: {
      bg: colors.red[900],
      bgHover: colors.red[800],
      text: colors.red[200],
      border: colors.red[700],
      icon: colors.red[400],
    },
  },

  // Navigation colors - Using slate tones
  nav: {
    bg: '#1e293b', // slate-800
    bgHover: '#334155', // slate-700
    bgActive: '#1e40af', // blue-800 - softer active state
    text: colors.gray[300],
    textHover: colors.gray[100],
    textActive: colors.blue[400],
    border: '#334155', // slate-700
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.3)',
  },

  // Card colors - Using slate tones
  card: {
    bg: '#1e293b', // slate-800
    bgHover: '#1e293b',
    border: '#334155', // slate-700
    shadow: '0 1px 3px 0 rgba(0, 0, 0, 0.3)',
    shadowHover: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
    shadowElevated:
      '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    shadowLarge:
      '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  },

  // Section/Block separation
  section: {
    border: '#334155', // slate-700
    borderStrong: '#475569', // slate-600
    shadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    shadowStrong:
      '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    bg: '#1e293b', // slate-800
    bgSubtle: '#0f172a', // slate-900
  },

  // Modal colors - Using slate tones
  modal: {
    bg: '#0f172a', // slate-900 - darker for modals
    overlay: 'rgba(0, 0, 0, 0.7)',
    border: '#334155', // slate-700
    shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
  },

  // Table colors - Using slate tones
  table: {
    bg: '#1e293b', // slate-800
    bgHeader: '#334155', // slate-700
    bgRow: '#1e293b', // slate-800
    bgRowHover: '#334155', // slate-700
    bgRowSelected: '#1e40af', // blue-800 - softer selection
    border: '#334155', // slate-700
    text: colors.gray[100],
    textHeader: colors.gray[300],
  },

  // Badge colors
  badge: {
    default: {
      bg: colors.gray[700],
      text: colors.gray[300],
    },
    primary: {
      bg: colors.blue[900],
      text: colors.blue[300],
    },
    success: {
      bg: colors.green[900],
      text: colors.green[300],
    },
    warning: {
      bg: colors.amber[900],
      text: colors.amber[300],
    },
    error: {
      bg: colors.red[900],
      text: colors.red[300],
    },
    purple: {
      bg: colors.purple[900],
      text: colors.purple[300],
    },
    indigo: {
      bg: colors.indigo[900],
      text: colors.indigo[300],
    },
  },

  // Priority colors (for dark theme - vibrant backgrounds with white text)
  priority: {
    low: {
      bg: colors.green[500], // More green, vibrant green background
      text: colors.white,
    },
    medium: {
      bg: colors.amber[500], // Clearer amber background
      text: colors.white,
    },
    high: {
      bg: colors.orange[500], // More intense orange background
      text: colors.white,
    },
    urgent: {
      bg: colors.red[600], // Vibrant red background
      text: colors.white,
    },
  },

  // Chart colors (same as light theme for consistency)
  chart: {
    colors: [
      colors.blue[400],
      colors.green[400],
      colors.amber[400],
      colors.red[400],
      colors.purple[400],
      colors.cyan[400],
      colors.indigo[400],
      colors.emerald[400],
      colors.purple[300],
      colors.indigo[300],
    ],
  },

  // Work type colors (adjusted for dark theme)
  workType: {
    damage: colors.blue[400],
    bullage_neige: colors.cyan[400],
    bullage: colors.cyan[300],
    treuil: colors.purple[400],
    transport_materiel: colors.amber[400],
    club_sports: colors.emerald[400],
    esf: colors.green[400],
    ski_show: colors.cyan[500],
    grand_prix: colors.cyan[600],
    secours: colors.red[500],
    carburant: colors.green[300],
    panne: colors.red[400],
    formation: colors.purple[300],
    maintenance: colors.amber[500],
    deplacement_enneigeurs: colors.purple[500],
    deneigement_regards: colors.purple[300],
    point_organisation: colors.gray[400],
    debut_fin_poste: colors.gray[500],
    other: colors.gray[500],
  },
} as const

// ============================================================================
// THEME TYPE DEFINITIONS
// ============================================================================

export type ThemeMode = 'light' | 'dark'
export type LightTheme = typeof lightTheme
export type DarkTheme = typeof darkTheme
export type Theme = LightTheme | DarkTheme

// ============================================================================
// THEME GETTER FUNCTION
// ============================================================================

export const getTheme = (mode: ThemeMode): Theme => {
  return (mode === 'dark' ? darkTheme : lightTheme) as Theme
}

// ============================================================================
// EXPORT DEFAULT THEME
// ============================================================================

export const theme = {
  light: lightTheme,
  dark: darkTheme,
  colors,
  getTheme,
}

export default theme
